import { Component } from '@theme/component';

/** Number of frame requests in flight at once while a spin loads. */
const PARALLEL_LOADS = 12;

/**
 * Returns frame indexes in progressive order: every 10th frame, then every 5th, 2nd and finally the rest,
 * so a coarse rotation is draggable long before every frame has loaded.
 *
 * @param {number} total - The number of frames.
 * @returns {number[]}
 */
function progressiveOrder(total) {
  const seen = new Set();
  const order = [];

  for (const step of [10, 5, 2, 1]) {
    for (let index = 0; index < total; index += step) {
      if (seen.has(index)) continue;
      seen.add(index);
      order.push(index);
    }
  }

  return order;
}

/**
 * The decoded frames of one 360 spin, shared by every viewer showing it. The gallery is re-rendered on
 * each variant change, so caching here makes switching back to a colour instant.
 */
class FrameSet {
  /** @type {Map<string, FrameSet>} */
  static #cache = new Map();

  /**
   * @param {string[]} urls - The ordered frame URLs.
   * @returns {FrameSet}
   */
  static for(urls) {
    const key = `${urls.length}:${urls[0]}`;
    let frameSet = FrameSet.#cache.get(key);

    if (!frameSet) {
      frameSet = new FrameSet(urls);
      FrameSet.#cache.set(key, frameSet);
    }

    return frameSet;
  }

  /** @type {(ImageBitmap | HTMLImageElement | null)[]} */
  frames;

  /** Frames that finished loading, successfully or not. */
  settled = 0;

  /** @type {Set<(index: number) => void>} */
  listeners = new Set();

  #started = false;

  /**
   * @param {string[]} urls - The ordered frame URLs.
   */
  constructor(urls) {
    this.urls = urls;
    this.frames = new Array(urls.length).fill(null);
  }

  get total() {
    return this.urls.length;
  }

  get complete() {
    return this.settled === this.total;
  }

  load() {
    if (this.#started) return;
    this.#started = true;

    const queue = progressiveOrder(this.total);
    let next = 0;

    const worker = async () => {
      while (next < queue.length) {
        const index = /** @type {number} */ (queue[next++]);
        await this.#loadFrame(index);
      }
    };

    for (let i = 0; i < PARALLEL_LOADS; i++) worker();
  }

  /**
   * Returns the loaded frame closest to `index`, so dragging works while frames are still arriving.
   *
   * @param {number} index - The wanted frame index.
   * @returns {ImageBitmap | HTMLImageElement | null}
   */
  nearest(index) {
    for (let offset = 0; offset < this.total; offset++) {
      const before = this.frames[(index - offset + this.total) % this.total];
      if (before) return before;

      const after = this.frames[(index + offset) % this.total];
      if (after) return after;
    }

    return null;
  }

  /**
   * @param {number} index - The frame index to load.
   */
  async #loadFrame(index) {
    try {
      const image = new Image();
      image.decoding = 'async';
      image.src = /** @type {string} */ (this.urls[index]);
      await image.decode();
      this.frames[index] = 'createImageBitmap' in window ? await createImageBitmap(image) : image;
    } catch {
      // A missing frame is skipped; `nearest` falls back to its neighbours.
    }

    this.settled++;
    for (const listener of this.listeners) listener(index);
  }
}

/**
 * @typedef {object} Spin360Refs
 * @property {HTMLScriptElement} spinFrames - JSON list of frame URLs.
 * @property {HTMLCanvasElement} spinCanvas - The canvas the frames are drawn on.
 * @property {HTMLImageElement} [spinPoster] - The first frame, shown until the canvas is ready.
 * @property {HTMLElement} [spinProgress] - The loading progress track.
 * @property {HTMLElement} [spinProgressBar] - The loading progress bar.
 * @property {HTMLElement} [spinHint] - The "drag to rotate" hint.
 */

/**
 * A draggable 360° product spin drawn from an image frame sequence.
 *
 * @extends Component<Spin360Refs>
 */
class Spin360 extends Component {
  requiredRefs = ['spinFrames', 'spinCanvas'];

  /** @type {FrameSet | undefined} */
  #frameSet;

  #current = 0;
  #loading = false;
  #interacted = false;
  #autoplayed = false;

  /** @type {{ pointerId: number, startX: number, startFrame: number } | null} */
  #drag = null;

  /** @type {number | undefined} */
  #autoplayFrame;

  /**
   * Created on every connect: the slideshow temporarily moves slides in the DOM when jumping between
   * non-adjacent slides, so this element can be disconnected and reconnected many times.
   * @type {AbortController | undefined}
   */
  #controller;

  /** @type {IntersectionObserver | undefined} */
  #intersectionObserver;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();

    /** @type {string[]} */
    let urls = [];
    try {
      urls = JSON.parse(this.refs.spinFrames.textContent || '[]');
    } catch {
      urls = [];
    }
    if (urls.length < 2) return;

    this.#frameSet = FrameSet.for(urls);

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener('pointerdown', this.#handlePointerDown, { signal });
    this.addEventListener('pointermove', this.#handlePointerMove, { signal });
    this.addEventListener('pointerup', this.#handlePointerUp, { signal });
    this.addEventListener('pointercancel', this.#handlePointerUp, { signal });
    this.addEventListener('keydown', this.#handleKeyDown, { signal });

    this.#resizeObserver = new ResizeObserver(() => this.#resizeCanvas());
    this.#resizeObserver.observe(this);

    // Start loading once the spin is actually on screen (i.e. its slide is showing).
    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#intersectionObserver?.disconnect();
        this.#load();
      },
      { threshold: 0.25 }
    );
    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller?.abort();
    this.#intersectionObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    this.#stopAutoplay();
    if (this.#frameSet) this.#frameSet.listeners.delete(this.#handleFrameLoaded);

    // Reset so a reconnect (see #controller) starts listening and drawing again.
    this.#loading = false;
    this.#drag = null;
    this.removeAttribute('dragging');
  }

  #load() {
    const frameSet = this.#frameSet;
    if (!frameSet || this.#loading) return;
    this.#loading = true;

    frameSet.listeners.add(this.#handleFrameLoaded);

    if (frameSet.complete) {
      this.#draw();
      this.#maybeAutoplay();
    } else {
      this.refs.spinProgress?.removeAttribute('hidden');
      this.#updateProgress();
      if (frameSet.frames[0]) this.#draw();
      frameSet.load();
    }
  }

  /**
   * @param {number} index - The frame that just loaded.
   */
  #handleFrameLoaded = (index) => {
    const frameSet = this.#frameSet;
    if (!frameSet) return;

    this.#updateProgress();

    // Redraw when the frame on screen (or a closer neighbour of it) arrives.
    if (index === this.#current || this.refs.spinCanvas.hidden) this.#draw();

    if (frameSet.complete) {
      this.refs.spinProgress?.setAttribute('hidden', '');
      this.#maybeAutoplay();
    }
  };

  #updateProgress() {
    const frameSet = this.#frameSet;
    if (!frameSet || !this.refs.spinProgressBar) return;

    this.refs.spinProgressBar.style.width = `${Math.round((frameSet.settled / frameSet.total) * 100)}%`;
  }

  #resizeCanvas() {
    const canvas = this.refs.spinCanvas;
    const ratio = window.devicePixelRatio || 1;
    const width = Math.round(this.clientWidth * ratio);
    const height = Math.round(this.clientHeight * ratio);

    if (!width || !height || (canvas.width === width && canvas.height === height)) return;

    canvas.width = width;
    canvas.height = height;
    this.#draw();
  }

  #draw() {
    const frame = this.#frameSet?.nearest(this.#current);
    const canvas = this.refs.spinCanvas;
    const context = canvas.getContext('2d');
    if (!frame || !context || !canvas.width || !canvas.height) return;

    // Fit the frame inside the canvas (object-fit: contain).
    const scale = Math.min(canvas.width / frame.width, canvas.height / frame.height);
    const width = frame.width * scale;
    const height = frame.height * scale;

    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(frame, (canvas.width - width) / 2, (canvas.height - height) / 2, width, height);

    if (canvas.hidden) {
      canvas.hidden = false;
      this.refs.spinPoster?.setAttribute('hidden', '');
    }
  }

  /**
   * @param {number} index - The frame to show; wraps around in both directions.
   */
  #show(index) {
    const frameSet = this.#frameSet;
    if (!frameSet) return;

    this.#current = ((index % frameSet.total) + frameSet.total) % frameSet.total;
    this.setAttribute('aria-valuenow', String(this.#current + 1));
    this.#draw();
  }

  #markInteracted() {
    this.#interacted = true;
    this.#stopAutoplay();
    this.refs.spinHint?.setAttribute('hidden', '');
    this.#load();
  }

  /**
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    if (event.button !== 0 || !this.#frameSet) return;

    this.#markInteracted();
    this.setPointerCapture(event.pointerId);
    this.#drag = { pointerId: event.pointerId, startX: event.clientX, startFrame: this.#current };
    this.setAttribute('dragging', '');
    // preventDefault stops text selection and native image drag, but also the default focus on press.
    event.preventDefault();
    this.focus({ preventScroll: true });
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerMove = (event) => {
    const drag = this.#drag;
    const frameSet = this.#frameSet;
    if (!drag || !frameSet || event.pointerId !== drag.pointerId) return;

    // One full rotation per width of drag, clamped so very small or large viewers stay usable.
    const pixelsPerFrame = Math.min(10, Math.max(2, this.clientWidth / frameSet.total));
    const steps = Math.round((event.clientX - drag.startX) / pixelsPerFrame);

    this.#show(drag.startFrame - steps);
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerUp = (event) => {
    if (!this.#drag || event.pointerId !== this.#drag.pointerId) return;

    this.#drag = null;
    this.removeAttribute('dragging');
    if (this.hasPointerCapture(event.pointerId)) this.releasePointerCapture(event.pointerId);
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    const step = { ArrowLeft: 1, ArrowRight: -1 }[event.key];
    if (!step) return;

    // Keep the arrow keys from also moving the surrounding slideshow.
    event.preventDefault();
    event.stopPropagation();
    this.#markInteracted();
    this.#show(this.#current + step);
  };

  /** Rotates once after loading when `autoplay` is set, unless the shopper already interacted. */
  #maybeAutoplay() {
    const frameSet = this.#frameSet;
    if (!frameSet || this.#interacted || this.#autoplayed || !this.hasAttribute('autoplay')) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.#autoplayed = true;

    const duration = 3000;
    const startFrame = this.#current;
    const startTime = performance.now();

    const step = (/** @type {number} */ time) => {
      const progress = Math.min(1, (time - startTime) / duration);
      this.#show(startFrame + Math.round(progress * frameSet.total));
      this.#autoplayFrame = progress < 1 ? requestAnimationFrame(step) : undefined;
    };

    this.#autoplayFrame = requestAnimationFrame(step);
  }

  #stopAutoplay() {
    if (this.#autoplayFrame === undefined) return;
    cancelAnimationFrame(this.#autoplayFrame);
    this.#autoplayFrame = undefined;
  }
}

if (!customElements.get('spin-360')) {
  customElements.define('spin-360', Spin360);
}
